import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { PermissionService } from '../services/permission.service';
import { AuthService } from '../auth.service';
import { map, take } from 'rxjs/operators';
import { getAdminPermissions, getSuperAdminPermissions } from '../constants/permission-config';

/**
 * Route guard to protect admin routes
 * Only allows access if user has admin permissions (configured in permission-config)
 */
export const adminGuard: CanActivateFn = () => {
  const permissionService = inject(PermissionService);
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.user) {
    router.navigate(['/auth']);
    return false;
  }

  // Get admin permissions from configuration
  const adminPermissions = getAdminPermissions();
  const hasAdminAccess = permissionService.hasAnyPermission(adminPermissions) || permissionService.isViewer() || permissionService.isAdmin() || permissionService.isSuperAdmin();
  
  if (hasAdminAccess) {
    return true;
  }

  return permissionService.getUserProfile().pipe(
    take(1),
    map(userProfile => {
      if (!userProfile) {
        return true;
      }

      const hasAccess = permissionService.hasAnyPermission(adminPermissions);

      if (!hasAccess) {
        router.navigate(['/']);
      }

      return hasAccess;
    })
  );
};

/**
 * Route guard to protect super admin routes
 * Only allows access if user has super admin permissions (configured in permission-config)
 */
export const superAdminGuard: CanActivateFn = () => {
  const permissionService = inject(PermissionService);
  const authService = inject(AuthService);
  const router = inject(Router);

  if (!authService.user) {
    router.navigate(['/auth']);
    return false;
  }

  // Get super admin permissions from configuration
  const superAdminPermissions = getSuperAdminPermissions();
  const hasFullAccess = permissionService.hasAnyPermission(superAdminPermissions);

  if (hasFullAccess) {
    return true;
  }

  return permissionService.getUserProfile().pipe(
    take(1),
    map(userProfile => {
      if (!userProfile) {
        return false;
      }

      const hasAccess = permissionService.hasAnyPermission(superAdminPermissions);

      if (!hasAccess) {
        router.navigate(['/']);
      }

      return hasAccess;
    })
  );
};
